import { z } from 'zod';
import { paginationQuerySchema } from '../common/envelope.js';
/** The structured verdict an `AiQualificationProvider` must return — the
 * exact shape is validated against this schema before anything in the CRM
 * trusts it (never free-form text). A provider that returns malformed
 * JSON, or JSON that fails this schema, is treated as a provider failure
 * (`AiQualificationService` falls back to its "error" decision), not as
 * "no, not a lead" — those are different outcomes with different
 * downstream handling (a failure is retryable; a genuine "not a lead"
 * verdict isn't). */
export const AI_LEAD_CLASSIFICATIONS = [
    'SALES_LEAD',
    'EXISTING_CUSTOMER',
    'SUPPORT',
    'PARTNER',
    'VENDOR',
    'NEWSLETTER',
    'MARKETING',
    'SPAM',
    'INTERNAL',
    'AUTOMATED',
    'OTHER',
];
export const aiLeadClassificationSchema = z.enum(AI_LEAD_CLASSIFICATIONS);
export const AI_LEAD_PRIORITIES = ['HOT', 'WARM', 'COLD', 'NONE'];
export const aiLeadPrioritySchema = z.enum(AI_LEAD_PRIORITIES);
export const aiQualificationResultSchema = z.object({
    isLead: z.boolean(),
    leadType: aiLeadClassificationSchema,
    confidence: z.number().min(0).max(1),
    priority: aiLeadPrioritySchema,
    intent: z.string().trim().max(200).nullable(),
    reason: z.string().trim().min(1).max(1000),
    companyName: z.string().trim().max(200).nullable(),
    contactName: z.string().trim().max(200).nullable(),
    email: z.string().trim().email().nullable().or(z.literal('').transform(() => null)),
    phone: z.string().trim().max(50).nullable(),
    recommendedAction: z.string().trim().max(300).nullable(),
});
/** What `AiQualificationService` actually does with a result, after
 * applying the tenant's configured thresholds — `auto_create`/
 * `needs_review`/`rejected` from a successful classification,
 * `error` when the provider call itself failed (network, bad response,
 * schema validation failure) — never silently coerced into `rejected`,
 * since a down provider isn't the same thing as "genuinely not a lead". */
export const aiQualificationDecisionSchema = z.enum(['auto_create', 'needs_review', 'rejected', 'error']);
export const aiLeadQualificationResponseSchema = z.object({
    id: z.string().uuid(),
    source: z.string(),
    sourceRefId: z.string().nullable(),
    senderEmail: z.string().nullable(),
    senderName: z.string().nullable(),
    subject: z.string().nullable(),
    decision: aiQualificationDecisionSchema,
    classification: aiLeadClassificationSchema.nullable(),
    confidence: z.number().nullable(),
    priority: aiLeadPrioritySchema.nullable(),
    intent: z.string().nullable(),
    reason: z.string().nullable(),
    recommendedAction: z.string().nullable(),
    extractedCompanyName: z.string().nullable(),
    extractedContactName: z.string().nullable(),
    extractedEmail: z.string().nullable(),
    extractedPhone: z.string().nullable(),
    modelUsed: z.string().nullable(),
    errorMessage: z.string().nullable(),
    leadId: z.string().uuid().nullable(),
    contactId: z.string().uuid().nullable(),
    reviewedByMembershipId: z.string().uuid().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().datetime().nullable(),
    reviewOutcome: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const AI_REVIEW_OUTCOMES = ['approved_lead', 'rejected', 'marked_existing_customer', 'marked_spam', 'converted_contact', 'reclassified'];
export const aiReviewOutcomeSchema = z.enum(AI_REVIEW_OUTCOMES);
/** The review queue's own list filter — defaults to exactly what the
 * queue is for (`needs_review`, not yet acted on), but a reviewer can
 * widen it to see the full qualification history (approved/rejected/
 * errored) for audit purposes. */
export const listAiLeadQualificationsQuerySchema = paginationQuerySchema.extend({
    decision: aiQualificationDecisionSchema.optional(),
    reviewed: z.enum(['pending', 'reviewed']).optional(),
    /** Backs the "Junk" tab — items a reviewer explicitly marked as spam,
     * independent of `decision` (a `marked_spam` row can have started as
     * `needs_review` or even `auto_create` before a human corrected it). */
    reviewOutcome: aiReviewOutcomeSchema.optional(),
});
/** Backs both the sidebar's pending-count badge and the AI Lead Review
 * page's own stat tiles / breakdown bar. `pendingNeedsReview`/
 * `pendingFailed` are "still needs attention" counts (unreviewed,
 * all-time) — what the badge sums. `today` is every decision's volume
 * for the current day, all on the same time window, so the page can show
 * a clean proportional breakdown of what AI did today rather than mixing
 * an all-time backlog count with a single day's volume. */
export const aiQualificationCountsResponseSchema = z.object({
    pendingNeedsReview: z.number().int(),
    pendingFailed: z.number().int(),
    today: z.object({
        autoCreated: z.number().int(),
        needsReview: z.number().int(),
        rejected: z.number().int(),
        failed: z.number().int(),
    }),
});
export const reviewAiLeadQualificationInputSchema = z
    .object({
    outcome: aiReviewOutcomeSchema,
    /** Required only for `approved_lead` when the row isn't already linked
     * to a Lead (e.g. a `needs_review` row an auto-create never touched) —
     * the reviewer picks which existing Contact/Company to attach it to, if
     * any; omitted entirely just creates a fresh Lead from the extracted
     * fields, same shape `LeadsService.create` already accepts elsewhere. */
    contactId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    /** Required only for outcome='reclassified' — the reviewer's corrected
     * verdict. Overwrites the stored `classification` for audit/reporting
     * purposes; doesn't itself create a Lead/Contact (pair it with a
     * second review call using one of the other outcomes for that). */
    classification: aiLeadClassificationSchema.optional(),
})
    .refine((v) => v.outcome !== 'reclassified' || v.classification !== undefined, {
    message: "classification is required when outcome is 'reclassified'.",
    path: ['classification'],
});
/** A tenant-managed rule book `AiQualificationService.checkRules` reads
 * before ever calling a provider, checked in this priority order when more
 * than one rule matches the same message: `blacklist` short-circuits
 * straight to `rejected`; `whitelist` straight to `auto_create` (either
 * way, no AI call made at all — a known-good or known-bad sender is
 * instant and free, not subject to a model having an off day); `classify`
 * doesn't decide lead-or-not on its own — it forces `classification` and
 * sends the row to `needs_review` labeled, for shaping what AI Lead Review
 * shows rather than fully automating the decision. */
export const AI_QUALIFICATION_LIST_TYPES = ['blacklist', 'whitelist', 'classify'];
export const aiQualificationListTypeSchema = z.enum(AI_QUALIFICATION_LIST_TYPES);
/** What `pattern` is matched against — `email`/`domain` against the
 * sender's address, `phone` against the sender's phone number (digits
 * only, matched as a substring so a prefix like a country+area code
 * works), `keyword` as a case-insensitive substring of the subject+body. */
export const AI_QUALIFICATION_MATCH_TYPES = ['email', 'domain', 'phone', 'keyword'];
export const aiQualificationMatchTypeSchema = z.enum(AI_QUALIFICATION_MATCH_TYPES);
const emailPattern = z.string().trim().toLowerCase().email('Enter a full email address (e.g. "jane@example.com").');
const domainPattern = z
    .string()
    .trim()
    .toLowerCase()
    .refine((v) => /^@[a-z0-9.-]+\.[a-z]{2,}$/.test(v), 'Enter a domain starting with "@" (e.g. "@example.com").');
const phonePattern = z
    .string()
    .trim()
    .refine((v) => /^\+?\d{4,15}$/.test(v), 'Enter digits only (a leading "+" is fine), at least 4 digits — a number or a prefix.');
const keywordPattern = z.string().trim().min(2, 'Keyword must be at least 2 characters.').max(100);
const PATTERN_CHECK_BY_MATCH_TYPE = {
    email: emailPattern,
    domain: domainPattern,
    phone: phonePattern,
    keyword: keywordPattern,
};
export const createAiQualificationEmailRuleSchema = z
    .object({
    matchType: aiQualificationMatchTypeSchema,
    pattern: z.string().trim().min(1).max(255),
    listType: aiQualificationListTypeSchema,
    classification: aiLeadClassificationSchema.optional(),
    note: z.string().trim().max(300).optional(),
})
    .superRefine((v, ctx) => {
    if ((v.listType === 'classify') !== (v.classification !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'classification is required for a "classify" rule, and only for a "classify" rule.', path: ['classification'] });
    }
    const patternCheck = PATTERN_CHECK_BY_MATCH_TYPE[v.matchType].safeParse(v.pattern);
    if (!patternCheck.success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: patternCheck.error.issues[0]?.message ?? 'Invalid pattern for this match type.', path: ['pattern'] });
    }
});
export const aiQualificationEmailRuleResponseSchema = z.object({
    id: z.string().uuid(),
    matchType: aiQualificationMatchTypeSchema,
    pattern: z.string(),
    listType: aiQualificationListTypeSchema,
    classification: aiLeadClassificationSchema.nullable(),
    note: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const listAiQualificationEmailRulesQuerySchema = z.object({
    listType: aiQualificationListTypeSchema.optional(),
});
//# sourceMappingURL=schemas.js.map